<template>
    <div class="create-disease">
        <h3>Crear Nueva Enfermedad o Categoría</h3>
        <form @submit.prevent="createDisease">
            <Select2
v-model="selectedCategoryIndex" :options="categoryOptions"
                placeholder="Selecciona una categoría" />
            <input
v-model="localNewDiseaseName" type="text" placeholder="Nombre de la enfermedad" class="create-input"
                required />
            <input
v-model="newCategoryName" type="text" placeholder="Nombre de la nueva categoría (opcional)"
                class="create-input" />
            <button type="submit">Crear</button>
        </form>
    </div>
</template>

<script setup>
import { ref, computed, nextTick, watch } from 'vue';
import Select2 from '@/components/Select2.vue';
import api from '@/services/api';

const props = defineProps({
    expandedCards: {
        type: Array,
        required: true
    },
    newDiseaseName: {
        type: String,
        default: ''
    },
    patientRecord: {
        type: Object,
        required: true
    }
});

const emits = defineEmits(['updateExpandedCards', 'update:newDiseaseName', 'create-disease']);

// Creamos una copia local de expandedCards para evitar mutar el prop
const localExpandedCards = ref([...props.expandedCards]);

// Crear una variable local para newDiseaseName
const localNewDiseaseName = ref(props.newDiseaseName);
const selectedCategoryIndex = ref('');
const newCategoryName = ref('');

const categoryOptions = computed(() => {
    return localExpandedCards.value.map((category, index) => ({
        id: index,
        text: category.name
    }));
});

const createDisease = async () => {
    const categoryIndex = parseInt(selectedCategoryIndex.value, 10);

    if (categoryIndex >= 0 && categoryIndex < localExpandedCards.value.length) {
        const category = localExpandedCards.value[categoryIndex];
        const diseaseExists = category.diseases.some(disease => disease.name.toLowerCase() === localNewDiseaseName.value.toLowerCase());
        if (diseaseExists) {
            alert('La enfermedad ya existe en esta categoría.');
            return;
        }

        // Crear la nueva enfermedad en la API
        try {
            const response = await api.post('/diseases', {
                category_id: category.id,
                name_disease: localNewDiseaseName.value,
                name_category: category.name
            });

            const newDisease = response.data.data.disease;
            category.diseases.push({
                id: newDisease.id,
                name: newDisease.name,
                description: '',
                isOpen: true,
                isDeleted: false,
                family_members: [],
                active: true
            });
        } catch (error) {
            console.error('Error al crear la enfermedad:', error);
            alert('Hubo un error al crear la enfermedad.');
            return;
        }
    } else if (newCategoryName.value) {
        const categoryExists = localExpandedCards.value.some(category => category.name.toLowerCase() === newCategoryName.value.toLowerCase());
        if (categoryExists) {
            alert('La categoría ya existe.');
            return;
        }

        // Crear la nueva categoría y enfermedad en la API
        try {
            const response = await api.post('/diseases', {
                category_id: null, // Asumimos que la API crea una nueva categoría si category_id es null
                name_disease: localNewDiseaseName.value,
                name_category: newCategoryName.value
            });

            const newCategory = response.data.data.category;
            const newDisease = response.data.data.disease;
            localExpandedCards.value.push({
                id: newCategory.id,
                name: newCategory.name,
                diseases: [{
                    id: newDisease.id,
                    name: newDisease.name,
                    description: '',
                    isOpen: true,
                    isDeleted: false,
                    family_members: [],
                    active: true
                }]
            });
        } catch (error) {
            console.error('Error al crear la categoría y enfermedad:', error);
            alert('Hubo un error al crear la categoría y enfermedad.');
            return;
        }
    }

    // Actualizar el localStorage de expandedCards
    localStorage.setItem('expandedCards', JSON.stringify(localExpandedCards.value));

    // Actualizar el localStorage del paciente
    const patientData = JSON.parse(localStorage.getItem(`patient_${props.patientRecord.id}`)) || {};
    patientData.diseases = localExpandedCards.value.flatMap(category =>
        category.diseases.map(disease => ({
            id: disease.id,
            name: disease.name,
            category_id: category.id,
            family_members: disease.family_members,
            description: disease.description,
            active: disease.active,
            pivot: {
                patient_id: props.patientRecord.id,
                disease_id: disease.id,
                family_members: JSON.stringify(disease.family_members),
                description: disease.description,
                created_at: disease.created_at,
                updated_at: disease.updated_at
            }
        }))
    );
    localStorage.setItem(`patient_${props.patientRecord.id}`, JSON.stringify(patientData));

    //console.log('Enfermedad creada:', localNewDiseaseName.value);

    localNewDiseaseName.value = '';
    selectedCategoryIndex.value = '';
    newCategoryName.value = '';

    // Emitimos un evento para notificar que se ha creado una enfermedad
    emits('create-disease');
    // Emitimos un evento al padre para actualizar expandedCards
    emits('updateExpandedCards', localExpandedCards.value);

    await nextTick();
};

// Emitir un evento cuando cambie localNewDiseaseName
watch(localNewDiseaseName, (newValue) => {
    emits('update:newDiseaseName', newValue);
});
</script>

<style scoped>
.create-disease {
    margin-top: 20px;
}

.create-input {
    width: 100%;
    margin-bottom: 10px;
}

button {
    padding: 10px;
    background-color: #344767;
    color: white;
    border: none;
    cursor: pointer;
}
</style>
